import React, { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import Button from '../../components/button/Button';
import DropDown from '../../components/dropdown/DropDown';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import {
  getGuaranteeWeighingData,
  getGduWeightingFactors,
  recalculateCasfogWeighting,
  applyManualWeighting,
} from '../../services/GuaranteesService';
import { formatCurrency, formatMoney } from '../../utils/Masks';
import { toNumber } from '../../utils/Utils';
import { formatBooleanLabel, isManualWeightingState } from '../../utils/guaranteeDisplayUtils';

interface MonetizationWeighingTabProps {
  guaranteeId: string;
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';
  refreshKey?: number;
  onWeighingUpdated?: () => void;
}

type WeighingApiType = 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements';

const SECTION_HEADER_STYLE: React.CSSProperties = {
  backgroundColor: '#E6F5F0',
  padding: '12px 16px',
  borderRadius: '8px',
  margin: '0 0 16px 0',
};

const MANUAL_CALCULATION_CONTROL_HEIGHT = '52px';

const MANUAL_CALCULATION_BUTTON_STYLE: React.CSSProperties = {
  height: MANUAL_CALCULATION_CONTROL_HEIGHT,
  minHeight: MANUAL_CALCULATION_CONTROL_HEIGHT,
  backgroundColor: '#03BAA1',
  color: '#FFFFFF',
  fontWeight: 400,
};

const toApiType = (t: MonetizationWeighingTabProps['guaranteeType']): WeighingApiType => {
  switch (t) {
    case 'loan':                  return 'loans';
    case 'check':                 return 'checks';
    case 'promissory_note':       return 'promissory-notes';
    case 'commercial_endorsement': return 'commercial-endorsements';
  }
};

const WEIGHING_PILL_BASE_STYLE: React.CSSProperties = {
  borderRadius: '6px',
  padding: '4px 12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  justifySelf: 'start',
};

const WEIGHING_PILL_TEXT_STYLE: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  margin: 0,
};

const WEIGHING_PILL_DOT_BASE: React.CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  flexShrink: 0,
};

const formatWeighingImporte = (value: string | number | null | undefined): string => {
  if (value == null || value === '') return '-';
  const num = typeof value === 'number' ? value : toNumber(String(value));
  if (Number.isNaN(num)) return '-';
  return formatMoney(num);
};

const MonetizationWeighingTab: React.FC<MonetizationWeighingTabProps> = ({
  guaranteeId,
  guaranteeType,
  refreshKey = 0,
  onWeighingUpdated,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [gduFactors, setGduFactors] = useState<{ id: number; name: string }[]>([]);
  const [selectedFactorId, setSelectedFactorId] = useState<number | ''>('');
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isManualSaving, setIsManualSaving] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchWeighingData();
    fetchGduFactors();
  }, [guaranteeId, guaranteeType, refreshKey]);

  const fetchWeighingData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getGuaranteeWeighingData(toApiType(guaranteeType), guaranteeId);
      if (response.success && 'data' in response) {
        setData(response.data);
      } else {
        setError('Error al cargar los datos de monetización y ponderación');
      }
    } catch {
      setError('Error al cargar los datos de monetización y ponderación');
    } finally {
      setLoading(false);
    }
  };

  const fetchGduFactors = async () => {
    try {
      const factors = await getGduWeightingFactors();
      setGduFactors(factors);
    } catch {}
  };

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    setActionMessage(null);
    try {
      await recalculateCasfogWeighting(toApiType(guaranteeType), guaranteeId);
      await fetchWeighingData();
      onWeighingUpdated?.();
    } catch {
      setActionMessage('Error al reintentar la consulta a CASFOG. Intente nuevamente.');
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleManualCalculate = async () => {
    if (selectedFactorId === '') return;
    setIsManualSaving(true);
    setActionMessage(null);
    try {
      await applyManualWeighting(toApiType(guaranteeType), guaranteeId, selectedFactorId as number);
      await fetchWeighingData();
      onWeighingUpdated?.();
    } catch {
      setActionMessage('Error al guardar la ponderación manual. Intente nuevamente.');
    } finally {
      setIsManualSaving(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-AR');
  };

  const formatMonetizationDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    return formatDate(dateString);
  };

  const renderSectionHeader = (title: string) => (
    <div style={SECTION_HEADER_STYLE}>
      <H variant="secondary-2" type="4" weight="bold" modifier="strong" withMargin={false}>
        {title}
      </H>
    </div>
  );

  const formatFieldValue = (
    value: string | number | boolean | null | undefined,
    options?: { isCurrency?: boolean; isWeighingImporte?: boolean; isBoolean?: boolean; allowEmpty?: boolean }
  ) => {
    if (value == null || value === '') return options?.allowEmpty ? '' : '-';
    if (options?.isBoolean) return formatBooleanLabel(value);
    if (options?.isWeighingImporte) return formatWeighingImporte(value as string | number);
    if (options?.isCurrency) return formatCurrency(value as string | number);
    return String(value);
  };

  const InfoField = ({
    label,
    value,
    isCurrency = false,
    isWeighingImporte = false,
    isBoolean = false,
    allowEmpty = false,
  }: {
    label: string;
    value: string | number | boolean | null | undefined;
    isCurrency?: boolean;
    isWeighingImporte?: boolean;
    isBoolean?: boolean;
    allowEmpty?: boolean;
  }) => (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'baseline' }}>
      <P type='3' variant='neutral' weight='bold' withMargin={false}>
        {label}:
      </P>
      <P type='3' variant='neutral' withMargin={false}>
        {formatFieldValue(value, { isCurrency, isWeighingImporte, isBoolean, allowEmpty })}
      </P>
    </div>
  );

  if (loading) {
    return (
      <Card margin={{ top: '4' }}>
        <TableSkeleton rows={4} columns={4} />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card display='flex' justifyContent='center' alignItems='center' height='200px'>
        <P type="1" weight="regular" modifier="weak">{error || 'No hay datos disponibles'}</P>
      </Card>
    );
  }

  const isCertificadoPyme: boolean = data.is_certificado_pyme === true;
  const isManualWeighting = isManualWeightingState(data);
  const showWeighingValues = isCertificadoPyme || isManualWeighting;
  const selectedFactor = gduFactors.find((factor) => factor.id === selectedFactorId);
  const categoryDropdownTitle = selectedFactor?.name ?? 'Seleccionar categoría';

  const handleCategoryChange = (label: string) => {
    const factor = gduFactors.find((item) => item.name === label);
    setSelectedFactorId(factor ? factor.id : '');
  };

  const renderExecutionDataLoan = () => (
    <>
      {renderSectionHeader('Datos de ejecucion')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InfoField
          label="Fecha de monetizacion"
          value={formatMonetizationDate(data.monetization_date)}
          allowEmpty
        />
        <InfoField label="Ejecutado" value={data.is_executed === true || data.is_executed === 'true' ? 'Sí' : 'No'} />
      </div>
    </>
  );

  const renderExecutionDataCheck = () => (
    <>
      {renderSectionHeader('Datos de ejecucion')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InfoField
          label="Fecha de monetizacion"
          value={formatMonetizationDate(data.monetization_date)}
          allowEmpty
        />
        <InfoField label="Codigo CVBA" value={data.monetization_cvba_code} />
        <InfoField label="Monto total en pesos" value={data.monetization_amount} isCurrency />
        <InfoField label="IVA" value={data.iva} isBoolean />
        <InfoField label="Tasa de descuento" value={data.monetization_discount_rate} />
        <InfoField label="Intereses" value={data.monetization_interest} isCurrency />
        <InfoField label="Fecha de liquidacion" value={formatDate(data.settlement_date)} />
        <InfoField label="Plazo de liquidacion" value={data.clearing} />
        <InfoField label="Alyc compradora" value={data.buyer_alyc} />
        <InfoField label="Alyc Vendedora" value={data.seller_alyc} />
        <InfoField label="Ejecutado" value={data.is_executed === true || data.is_executed === 'true' ? 'Sí' : 'No'} />
      </div>
    </>
  );

  const renderExecutionDataPromissoryNote = () => (
    <>
      {renderSectionHeader('Datos de ejecucion')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InfoField
          label="Fecha de monetizacion"
          value={formatMonetizationDate(data.monetization_date)}
          allowEmpty
        />
        <InfoField label="Codigo CVBA" value={data.monetization_cvba_code} />
        <InfoField label="IVA" value={data.iva} isBoolean />
        <InfoField label="Tasa de descuento" value={data.monetization_discount_rate} />
        <InfoField label="Intereses" value={data.monetization_interest} isCurrency />
        <InfoField label="Fecha de liquidacion" value={formatDate(data.settlement_date)} />
        <InfoField label="Plazo de liquidacion" value={data.clearing} />
        <InfoField label="Alyc compradora" value={data.buyer_alyc} />
        <InfoField label="Alyc Vendedora" value={data.seller_alyc} />
        <InfoField label="Ejecutado" value={data.is_executed === true || data.is_executed === 'true' ? 'Sí' : 'No'} />
      </div>
    </>
  );

  const renderExecutionDataCommercialEndorsement = () => (
    <>
      {renderSectionHeader('Datos de ejecucion')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <InfoField
          label="Fecha de monetizacion"
          value={formatMonetizationDate(data.monetization_date)}
          allowEmpty
        />
        <InfoField label="Monto total en pesos" value={data.monetization_amount} isCurrency />
        <InfoField label="Ejecutado" value={data.is_executed === true || data.is_executed === 'true' ? 'Sí' : 'No'} />
      </div>
    </>
  );

  const renderWeighingStatusPill = () => {
    if (isCertificadoPyme) {
      return (
        <div
          style={{ ...WEIGHING_PILL_BASE_STYLE, backgroundColor: '#E7F1EC' }}
          data-testid="weighing-status-pill"
          data-variant="casfog"
        >
          <span style={{ ...WEIGHING_PILL_DOT_BASE, backgroundColor: '#0C7339' }} aria-hidden="true" />
          <span style={{ ...WEIGHING_PILL_TEXT_STYLE, color: '#0C7339' }} data-testid="weighing-status-pill-text">
            Certificado PyME Vigente (CASFOG)
          </span>
        </div>
      );
    }
    if (isManualWeighting) {
      return (
        <div
          style={{ ...WEIGHING_PILL_BASE_STYLE, backgroundColor: '#D1ECF1' }}
          data-testid="weighing-status-pill"
          data-variant="manual"
        >
          <span style={{ ...WEIGHING_PILL_DOT_BASE, backgroundColor: '#17A2B8' }} aria-hidden="true" />
          <span style={{ ...WEIGHING_PILL_TEXT_STYLE, color: '#1F2937' }} data-testid="weighing-status-pill-text">
            Ponderación Ejecutada Manualmente
          </span>
        </div>
      );
    }
    return null;
  };

  const renderWeighingSection = () => (
    <>
      {renderSectionHeader('Ponderación')}

      {!showWeighingValues && (
        <div style={{
          backgroundColor: '#FFF3CD',
          border: '1px solid #FFC107',
          borderRadius: '4px',
          padding: '10px 14px',
          marginTop: '12px',
          marginBottom: '12px',
        }}>
          <P type='3' variant='neutral' withMargin={false}>
            No se obtuvo el certificado PyME desde CASFOG. La ponderación deberá calcularse manualmente o reintentando la consulta.
          </P>
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={handleRecalculate}
              disabled={isRecalculating}
              style={{
                padding: '6px 14px',
                backgroundColor: '#FFC107',
                border: 'none',
                borderRadius: '4px',
                cursor: isRecalculating ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
              }}
            >
              {isRecalculating ? 'Consultando...' : 'Recalcular ponderación'}
            </button>
          </div>
        </div>
      )}

      {showWeighingValues && (
        <>
          <div
            data-testid="weighing-values-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(max-content, 1fr) repeat(3, 1fr)',
              alignItems: 'center',
              gap: '16px',
              marginTop: '8px',
              marginBottom: '16px',
            }}
          >
            {renderWeighingStatusPill()}
            <InfoField label="Porcentaje" value={data.weighting_percentage} />
            <InfoField label="Importe" value={data.weighting_amount} isWeighingImporte />
            <InfoField label="Categoria" value={data.category} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <InfoField label="Ponderador aplicable" value={data.applicable_weigher} />
            {data.monetization_accounting_receipt && (
              <InfoField label="Comprobante contable de monetizacion" value={data.monetization_accounting_receipt} />
            )}
          </div>
        </>
      )}

      <div style={{ marginTop: '8px' }}>
        <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
          Cálculo manual
        </H>
        <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginTop: '8px', marginBottom: '16px' }}>
          Seleccioná la categoría PyME. Al calcular, el resultado reemplaza al instante el valor de ponderación obtenido de CASFOG.
        </P>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '240px', flex: '1 1 240px', maxWidth: '360px' }}>
            <DropDown
              id="manual-weighting-category"
              label="Categoría Pyme"
              title={categoryDropdownTitle}
              placeholderTitle="Seleccionar categoría"
              items={gduFactors.map((factor) => ({ id: String(factor.id), label: factor.name }))}
              onChange={handleCategoryChange}
              size="md"
            />
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={handleManualCalculate}
            disabled={isManualSaving || selectedFactorId === ''}
            style={{
              ...MANUAL_CALCULATION_BUTTON_STYLE,
              opacity: isManualSaving || selectedFactorId === '' ? 0.65 : 1,
            }}
          >
            {isManualSaving ? 'Guardando...' : 'Calcular y reemplazar'}
          </Button>
        </div>
        {actionMessage && (
          <P type='3' variant='neutral' withMargin={false} style={{ marginTop: '8px', color: '#DC3545' }}>
            {actionMessage}
          </P>
        )}
      </div>
    </>
  );

  return (
    <Card>
      {guaranteeType === 'loan' && renderExecutionDataLoan()}
      {guaranteeType === 'check' && renderExecutionDataCheck()}
      {guaranteeType === 'promissory_note' && renderExecutionDataPromissoryNote()}
      {guaranteeType === 'commercial_endorsement' && renderExecutionDataCommercialEndorsement()}

      {renderWeighingSection()}
    </Card>
  );
};

export default MonetizationWeighingTab;
